import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { SERVICE_TYPE_OPTIONS } from '../data/serviceTypes';
import { formatUSPhoneInput, getUSPhoneError, toE164US } from '../utils/phone';
import { getViewerTimezone } from '../utils/dateTime';
// Reuses InviteSomeonePage's stylesheet (isv-* classes) — same "invite form"
// look, no need for a parallel set of styles for a page that's structurally
// the same form with the role/vendor-type choices locked down.
import './InviteSomeonePage.css';

// Per-user cache of the SP vendorType, written by SpDashboardPage.js after
// it fetches /sp-profile to decide whether to show the "Invite Inspector"
// quick action at all. Reading the same cache here lets this page render
// immediately in the (overwhelmingly common) case of arriving via that
// button — the access-guard effect below still re-verifies against the API
// and can redirect away if the cached value turns out stale.
const vendorTypeCacheKey = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    return stored?.email ? `spVendorType:${stored.email}` : null;
  } catch {
    return null;
  }
};

const getCachedVendorType = () => {
  const key = vendorTypeCacheKey();
  return key ? localStorage.getItem(key) : null;
};

const setCachedVendorType = (vendorType) => {
  const key = vendorTypeCacheKey();
  if (!key) return;
  if (vendorType) localStorage.setItem(key, vendorType);
  else localStorage.removeItem(key);
};

// Company and Individual SPs may both use this page. Anything else (no
// vendorType yet, unknown value) is sent back to the SP dashboard.
const isInviteAllowedVendorType = (vendorType) =>
  vendorType === 'COMPANY' || vendorType === 'INDIVIDUAL';

// Roles a Company SP can pick from — same list and labels as
// InviteSomeonePage's Agent/Broker set (backend: InvitationService gives
// Company SPs AGENT_BROKER_ALLOWED_INVITE_ROLES). Individual SPs never see
// this; their page stays the fixed Vendor/Individual "Invite Inspector" form.
const COMPANY_INVITE_ROLES = [
  { label: 'Agent', value: 'AGENT' },
  { label: 'Broker', value: 'BROKER' },
  { label: 'Broker Manager', value: 'BROKER_MANAGER' },
  { label: 'Broker Admin', value: 'BROKER_ADMIN' },
  { label: 'Brokerage', value: 'BROKERAGE' },
  { label: 'Buyer', value: 'BUYER' },
  { label: 'Seller', value: 'SELLER' },
  { label: 'Transaction Coordinator', value: 'TRANSACTION_COORDINATOR' },
  { label: 'Vendor', value: 'SERVICE_PROVIDER' },
];

// "Invite Inspector" — a Service Provider inviting an Individual vendor. When
// the inviter is a Company-vendorType SP, the invitee becomes linked to their
// company on acceptance (see InvitationService.linkServiceProviderToCompany on
// the backend, which re-checks the inviter is a COMPANY); an Individual SP's
// invite is a plain vendor invite with no company link. For an Individual SP
// the role (Vendor) and vendor type (Individual) are fixed and never shown
// as a choice. A Company SP instead gets an "Invite Someone" role picker
// (COMPANY_INVITE_ROLES); picking Vendor there adds a Vendor Type choice —
// Individual (the same invite as above, linked to the company) or Company
// (business name instead of first/last name, not linked).
const InviteInspectorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Active tab ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('direct'); // 'direct' | 'shareable'

  // Company SP only — no default role, user must pick one (same as InviteSomeonePage).
  const [selectedRole, setSelectedRole] = useState('');
  // Brokerage-specific fields (Company SP with selectedRole === 'BROKERAGE')
  const [brokerageCompanyName, setBrokerageCompanyName] = useState('');
  const [brokerageLicenceId, setBrokerageLicenceId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [vendorServiceType, setVendorServiceType] = useState('');
  // Vendor Type — Company SP only (Individual SP is always INDIVIDUAL and never
  // sees the picker). Individual (default) or Company (business name replaces
  // first/last name), same as InviteSomeonePage.
  const [vendorType, setVendorType] = useState('INDIVIDUAL');
  const [vendorBusinessName, setVendorBusinessName] = useState('');
  const [fullServiceTypeCatalog, setFullServiceTypeCatalog] = useState(SERVICE_TYPE_OPTIONS);
  // Company's own offered service types (name strings, from /sp-profile — see
  // access-guard effect below). null = not loaded yet; [] = loaded but the
  // company hasn't configured any. Both cases fall back to the full catalog
  // in serviceTypeOptions below so the picker is never empty/broken.
  const [companyServiceTypeNames, setCompanyServiceTypeNames] = useState(null);
  const [welcomeNote, setWelcomeNote] = useState('');
  const [contactMode, setContactMode] = useState('Both');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [pendingResendEmail, setPendingResendEmail] = useState(null);

  // ── Shareable Link state ──────────────────────────────────────────────────────
  // Individual SP: role and vendor type are fixed (SERVICE_PROVIDER /
  // INDIVIDUAL — see file header comment), so there's no picker, just the
  // maximum-uses choice. Company SP: role picker, plus Vendor Type for Vendor.
  const [shareableRole, setShareableRole] = useState('');
  const [shareableVendorType, setShareableVendorType] = useState('INDIVIDUAL');
  // Optional for shareable Company-vendor links (registrant enters it on the
  // register form) — same rule as InviteSomeonePage; sent only when non-blank.
  const [shareableBusinessName, setShareableBusinessName] = useState('');
  const [usageLimit, setUsageLimit] = useState('single'); // 'single' | 'unlimited'
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkExpiryDate, setLinkExpiryDate] = useState(null);
  const [shareableLinkCopied, setShareableLinkCopied] = useState(false);
  const [shareableError, setShareableError] = useState('');

  // Company vendorType SPs have no personal firstName/lastName — only a
  // businessName — so `inviterName` below needs this as a fallback or the
  // invite email send 400s server-side (UserInviteEmailRequest.inviterName
  // is @NotBlank). Captured from the same /sp-profile call the access guard
  // below already makes, no extra request.
  const [spBusinessName, setSpBusinessName] = useState(null);
  // Inviter's own vendorType (COMPANY | INDIVIDUAL) — drives the subtitle and
  // whether the service-type picker is narrowed to the company's offerings.
  const [spVendorType, setSpVendorType] = useState(getCachedVendorType);
  // Optimistically true when we already know (from the dashboard's cache)
  // that this user is a Company or Individual SP, so the form paints on the
  // same tick instead of blanking out for the access-guard round trip below.
  const [accessChecked, setAccessChecked] = useState(() => {
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() !== 'SERVICE_PROVIDER') return false;
    return isInviteAllowedVendorType(getCachedVendorType());
  });

  const getFirebaseToken = () => new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
      return;
    }
    const timeoutId = setTimeout(() => { unsubscribe(); reject(new Error('Firebase auth timeout')); }, 10000);
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      clearTimeout(timeoutId);
      unsubscribe();
      if (user) {
        try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
      } else {
        reject(new Error('No authenticated user'));
      }
    });
  });

  // Access guard: Service Provider role only, and only the Company or
  // Individual vendor types. Mirrors the role guard in
  // SpDashboardPage.js; the vendorType check needs its own fetch since it
  // isn't in localStorage (see SpDashboardHeader.js, which fetches the same
  // way for the same reason).
  useEffect(() => {
    let cancelled = false;
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() !== 'SERVICE_PROVIDER') {
      navigate('/dashboard', { replace: true });
      return;
    }
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.spProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const vendorType = resp.data?.vendorType || null;
        setCachedVendorType(vendorType);
        setSpVendorType(vendorType);
        setSpBusinessName(resp.data?.businessName || null);
        setCompanyServiceTypeNames(Array.isArray(resp.data?.serviceTypes) ? resp.data.serviceTypes : []);
        if (!isInviteAllowedVendorType(vendorType)) {
          navigate('/dashboard/sp', { replace: true });
          return;
        }
        setAccessChecked(true);
      } catch (error) {
        console.error('[InviteInspector] Failed to verify vendor access:', error);
        if (!cancelled) navigate('/dashboard/sp', { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill email when navigated here with a prefill (mirrors InviteSomeonePage).
  useEffect(() => {
    const prefill = location.state?.prefillEmail;
    if (prefill) setEmail(prefill);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Canonical service-type catalog — same fetch as InviteSomeonePage, same
  // offline-fallback seeding.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.serviceTypes, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const catalog = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        if (!cancelled && catalog.length > 0) {
          setFullServiceTypeCatalog(catalog.map(t => ({ id: t.id, name: t.label })));
        }
      } catch (error) {
        console.warn('[InviteInspector] Failed to fetch service types, using fallback:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // For a Company inviter, narrows to the company's own offered service types
  // once loaded (the invitee joins that company). An Individual inviter isn't
  // bringing anyone into a company, so they get the full catalog. Also falls
  // back to the full catalog while company data hasn't loaded yet, or if the
  // company has none configured, so the picker is never empty.
  const serviceTypeOptions = useMemo(() => {
    if (spVendorType !== 'COMPANY' || !companyServiceTypeNames || companyServiceTypeNames.length === 0) {
      return fullServiceTypeCatalog;
    }
    const filtered = fullServiceTypeCatalog.filter(opt => companyServiceTypeNames.includes(opt.name));
    return filtered.length > 0 ? filtered : fullServiceTypeCatalog;
  }, [fullServiceTypeCatalog, companyServiceTypeNames, spVendorType]);

  // Company SPs pick any role ("Invite Someone") and, for Vendor, a Vendor
  // Type; Individual SPs are locked to an Individual Vendor ("Invite Inspector").
  const isCompanyInviter = spVendorType === 'COMPANY';
  const inviteRole = isCompanyInviter ? selectedRole : 'SERVICE_PROVIDER';
  const isVendorInvite = inviteRole === 'SERVICE_PROVIDER';
  const isBrokerageInvite = inviteRole === 'BROKERAGE';
  const isCompanyVendor = isCompanyInviter && isVendorInvite && vendorType === 'COMPANY';
  const shareableInviteRole = isCompanyInviter ? shareableRole : 'SERVICE_PROVIDER';
  const isShareableCompanyVendor = isCompanyInviter && shareableInviteRole === 'SERVICE_PROVIDER'
    && shareableVendorType === 'COMPANY';
  // A Company invitee doesn't join the inviter's company, so it gets the full
  // catalog rather than the company-narrowed list.
  const vendorServiceTypeOptions = isCompanyVendor ? fullServiceTypeCatalog : serviceTypeOptions;

  const getLoggedInUser = () => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  };

  const validateFields = () => {
    const errors = {};
    if (isCompanyInviter && !selectedRole) errors.role = 'Please select a role';
    if (isBrokerageInvite) {
      if (!brokerageCompanyName.trim()) errors.brokerageCompanyName = 'Company name is required';
    } else if (isCompanyVendor) {
      if (!vendorBusinessName.trim()) errors.vendorBusinessName = 'Business name is required';
      const phoneErr = getUSPhoneError(phone, { required: contactMode !== 'Email' });
      if (phoneErr) errors.phone = phoneErr;
    } else {
      if (!firstName.trim()) errors.firstName = 'First name is required';
      if (!lastName.trim()) errors.lastName = 'Last name is required';
      const phoneErr = getUSPhoneError(phone, { required: contactMode !== 'Email' });
      if (phoneErr) errors.phone = phoneErr;
    }
    if (!email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    if (isVendorInvite && !vendorServiceType) errors.vendorServiceType = 'Please select a service type';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Direct-invite createInvitation body. The Individual-vendor branch is the
  // original Invite Inspector payload, unchanged.
  const buildDirectInvitePayload = (phoneValue) => {
    if (isBrokerageInvite) {
      return {
        role: 'BROKERAGE', isShareable: false,
        email: email.trim(),
        companyName: brokerageCompanyName.trim(),
        brokerageLicenseId: brokerageLicenceId.trim() || undefined,
      };
    }
    if (isCompanyVendor) {
      return {
        role: 'SERVICE_PROVIDER', isShareable: false, vendorType: 'COMPANY',
        email: email.trim(), phone: phoneValue,
        businessName: vendorBusinessName.trim(),
        serviceType: vendorServiceType,
      };
    }
    const payload = {
      role: inviteRole, isShareable: false,
      email: email.trim(), phone: phoneValue,
      firstName: firstName.trim(), lastName: lastName.trim(),
    };
    if (isVendorInvite) {
      payload.vendorType = 'INDIVIDUAL';
      payload.serviceType = vendorServiceType;
    }
    return payload;
  };

  const handleFieldChange = (field, setter) => (e) => {
    setter(e.target.value);
    if (fieldErrors[field]) setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handlePhoneFieldChange = (setter) => (e) => {
    setter(formatUSPhoneInput(e.target.value));
    if (fieldErrors.phone) setFieldErrors(prev => { const n = { ...prev }; delete n.phone; return n; });
  };

  const resetForm = () => {
    setFirstName(''); setLastName(''); setEmail(''); setPhone('');
    setVendorServiceType(''); setWelcomeNote(''); setContactMode('Both');
    setBrokerageCompanyName(''); setBrokerageLicenceId('');
    setVendorType('INDIVIDUAL'); setVendorBusinessName('');
    setFieldErrors({}); setPendingResendEmail(null);
  };

  const sendInviteEmail = async (invitationKey, authHeaders, user, inviterName) => {
    await apiClient.post(API_ENDPOINTS.sendInviteEmail, {
      invitationKey,
      email: email.trim(),
      inviteeName: isBrokerageInvite
        ? brokerageCompanyName.trim()
        : isCompanyVendor
        ? vendorBusinessName.trim()
        : [firstName.trim(), lastName.trim()].filter(Boolean).join(' '),
      role: inviteRole,
      inviterName,
      inviterEmail: user?.email,
      inviterPhone: user?.phone,
      welcomeNote: welcomeNote.trim() || undefined,
    }, { headers: authHeaders });
  };

  const handleSendInvite = async () => {
    if (!validateFields()) return;
    setIsSending(true);
    setErrorMessage(''); setSuccessMessage(''); setPendingResendEmail(null);
    try {
      const firebaseToken = await getFirebaseToken();
      const user = getLoggedInUser();
      const inviterName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '')
        || spBusinessName || 'A Rialty user';
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const normalizedPhone = phone.trim() ? toE164US(phone) : '';

      const invitationResponse = await apiClient.post(API_ENDPOINTS.createInvitation,
        buildDirectInvitePayload(normalizedPhone || undefined), { headers: authHeaders });
      const invitationKey = invitationResponse.data?.invitationKey || invitationResponse.data?.key ||
        invitationResponse.data?.data?.invitationKey;

      if (invitationKey) {
        try { await sendInviteEmail(invitationKey, authHeaders, user, inviterName); }
        catch (emailErr) { console.warn('Email send failed but invitation was created:', emailErr); }
      }
      setSuccessMessage('Invitation sent successfully!');
      resetForm();
    } catch (error) {
      const rawMsg = error.response?.data?.message || error.response?.data?.error || error.message || '';
      if (rawMsg.toLowerCase().includes('pending invitation already exists')) {
        setErrorMessage(`A pending invitation already exists for ${email.trim()}. Click "Resend" below to resend the invitation email.`);
        setPendingResendEmail(email.trim());
        return;
      }
      setErrorMessage(rawMsg || 'Failed to send invitation. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleResendInvitation = async () => {
    setIsSending(true);
    setErrorMessage(''); setSuccessMessage('');
    try {
      const firebaseToken = await getFirebaseToken();
      const user = getLoggedInUser();
      const inviterName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '')
        || spBusinessName || 'A Rialty user';
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const response = await apiClient.post(API_ENDPOINTS.resendInvitation, { email: email.trim(), role: inviteRole }, { headers: authHeaders });
      const invitationKey = response.data?.invitationKey || response.data?.key || response.data?.data?.invitationKey;
      if (invitationKey) {
        try { await sendInviteEmail(invitationKey, authHeaders, user, inviterName); }
        catch (emailErr) { console.warn('Email send failed but invitation was resent:', emailErr); }
      }
      setSuccessMessage('Invitation resent successfully!');
      resetForm();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to resend invitation. Please try again.');
      setPendingResendEmail(null);
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyLink = async () => {
    if (!validateFields()) return;
    setLinkCopied(false); setErrorMessage(''); setIsCopyingLink(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const payload = buildDirectInvitePayload(phone.trim() ? toE164US(phone) : '');
      const response = await apiClient.post(API_ENDPOINTS.createInvitation, payload, { headers: authHeaders });
      const data = response.data || {};
      const inviteLink = data.link || data.inviteLink || data.invitationLink || data.url ||
        (() => {
          const key = data.invitationKey || data.key || data.data?.invitationKey || data.data?.key;
          return key ? `${window.location.origin}/invite?key=${key}&type=user` : null;
        })();
      if (!inviteLink) { setErrorMessage('Invitation created but no link was returned. Please try again.'); return; }
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      const rawMsg = err.response?.data?.message || err.response?.data?.error || '';
      if (rawMsg.toLowerCase().includes('pending invitation already exists')) {
        setErrorMessage(`A pending invitation already exists for ${email.trim()}. Use "Send Invite" → "Resend" instead.`);
        setPendingResendEmail(email.trim());
      } else {
        setErrorMessage(rawMsg || 'Failed to generate invite link. Please try again.');
      }
    } finally {
      setIsCopyingLink(false);
    }
  };

  // ── Shareable Link handlers ──────────────────────────────────────────────────
  const handleGenerateShareableLink = async () => {
    if (!shareableInviteRole) { setShareableError('Please select a role.'); return; }
    setIsGeneratingLink(true);
    setShareableError('');
    try {
      const firebaseToken = await getFirebaseToken();
      const payload = {
        role: shareableInviteRole, isShareable: true,
        expiresInDays: 7, usageLimit,
      };
      if (shareableInviteRole === 'SERVICE_PROVIDER') {
        payload.vendorType = isShareableCompanyVendor ? 'COMPANY' : 'INDIVIDUAL';
        if (isShareableCompanyVendor && shareableBusinessName.trim()) payload.businessName = shareableBusinessName.trim();
      }
      const response = await apiClient.post(API_ENDPOINTS.createInvitation, payload, {
        headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' },
      });
      const data = response.data || {};
      const link = data.invitationLink || data.link || data.inviteLink || data.url ||
        (() => {
          const key = data.invitationKey || data.key || data.data?.invitationKey;
          return key ? `${window.location.origin}/invite?key=${key}&type=user` : null;
        })();
      if (!link) { setShareableError('Link generated but no URL was returned. Please try again.'); return; }
      setGeneratedLink(link);
      // Backend may return this as a bare LocalDateTime (UTC digits, no 'Z') —
      // append it if missing so this isn't misparsed as browser-local time.
      setLinkExpiryDate(data.expiresAt
        ? new Date(/[Zz]|[+-]\d{2}:\d{2}$/.test(data.expiresAt) ? data.expiresAt : `${data.expiresAt}Z`)
        : null);
    } catch (err) {
      setShareableError(err.response?.data?.message || err.message || 'Failed to generate link. Please try again.');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyShareableLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setShareableLinkCopied(true);
      setTimeout(() => setShareableLinkCopied(false), 3000);
    } catch {
      setShareableError('Failed to copy link. Please copy it manually.');
    }
  };

  const handleShareLink = async () => {
    const roleLabel = shareableInviteRole === 'SERVICE_PROVIDER'
      ? (isShareableCompanyVendor ? 'a Vendor' : 'an Inspector')
      : `a ${COMPANY_INVITE_ROLES.find(r => r.value === shareableInviteRole)?.label || shareableInviteRole}`;
    const expiryStr = linkExpiryDate
      ? linkExpiryDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: getViewerTimezone() })
      : '';
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Rialty Invitation',
          text: `You're invited to join Rialty as ${roleLabel}!\n\nClick the link below to register:\n${generatedLink}${expiryStr ? `\n\nThis link expires on ${expiryStr}.` : ''}`,
          url: generatedLink,
        });
      } catch (err) {
        if (err.name !== 'AbortError') handleCopyShareableLink();
      }
    } else {
      handleCopyShareableLink();
    }
  };

  const handleNewLink = () => {
    setGeneratedLink('');
    setLinkExpiryDate(null);
    setShareableLinkCopied(false);
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSuccessMessage('');
    setErrorMessage('');
    setShareableError('');
  };

  const formatExpiry = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: getViewerTimezone() });
  };

  if (!accessChecked) {
    return null;
  }

  return (
    <div className="dashboard-exact-container">
      <main className="isv-main">
        <div className="isv-card">

          <div className="isv-header">
            <div>
              <h1 className="isv-title">{isCompanyInviter ? 'Invite Someone' : 'Invite Inspector'}</h1>
              <p className="isv-subtitle">
                {isCompanyInviter
                  ? 'Invite someone to join Rialty — vendors you invite join your company'
                  : 'Invite an individual vendor to join Rialty'}
              </p>
            </div>
          </div>

          <div className="isv-divider" />

          {/* Tab Selector */}
          <div className="isv-tabs">
            <button
              className={`isv-tab-btn ${activeTab === 'direct' ? 'isv-tab-btn-active' : ''}`}
              onClick={() => handleTabSwitch('direct')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              Direct Invite
            </button>
            <button
              className={`isv-tab-btn ${activeTab === 'shareable' ? 'isv-tab-btn-active' : ''}`}
              onClick={() => handleTabSwitch('shareable')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Shareable Link
            </button>
          </div>

          {/* ── TAB 1: Direct Invite ─────────────────────────────────────────── */}
          {activeTab === 'direct' && (
          <>
          {/* Role Selection — Company SP only */}
          {isCompanyInviter && (
            <div className="isv-section">
              <label className="isv-label">INVITE AS (SELECT ONE):</label>
              <div className="isv-roles-grid">
                {COMPANY_INVITE_ROLES.map((role) => (
                  <button
                    key={role.value}
                    className={`isv-role-btn ${selectedRole === role.value ? 'isv-role-btn-active' : ''}`}
                    onClick={() => { setSelectedRole(role.value); if (fieldErrors.role) setFieldErrors(prev => { const n = { ...prev }; delete n.role; return n; }); }}
                  >
                    {role.label}
                  </button>
                ))}
              </div>
              {fieldErrors.role && <span className="isv-field-error">{fieldErrors.role}</span>}
            </div>
          )}

          {/* Vendor Type — Company SP only, when Vendor is selected */}
          {isCompanyInviter && isVendorInvite && (
            <div className="isv-section">
              <label className="isv-label">VENDOR TYPE</label>
              <div className="isv-contact-modes">
                {[{ label: 'Individual', value: 'INDIVIDUAL' }, { label: 'Company', value: 'COMPANY' }].map((opt) => (
                  <button
                    key={opt.value}
                    className={`isv-mode-btn ${vendorType === opt.value ? 'isv-mode-btn-active' : ''}`}
                    onClick={() => {
                      if (opt.value === vendorType) return;
                      // Service-type list differs per vendor type (see vendorServiceTypeOptions)
                      setVendorType(opt.value);
                      setVendorServiceType('');
                      setFieldErrors({});
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="isv-contact-hint">
                {vendorType === 'COMPANY'
                  ? 'Invites a separate vendor company — not linked to your company'
                  : 'Individual vendors join your company'}
              </span>
            </div>
          )}

          {isVendorInvite && (
          <div className="isv-section">
            <label className="isv-label">SERVICE TYPE</label>
            <div className="isv-select-group">
              <select
                className={`isv-select ${fieldErrors.vendorServiceType ? 'isv-input-error' : ''}`}
                value={vendorServiceType}
                onChange={handleFieldChange('vendorServiceType', setVendorServiceType)}
              >
                <option value="">Select Service Type</option>
                {vendorServiceTypeOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
              <span className="isv-select-hint">
                {isCompanyVendor ? "Select the vendor's service type" : "Select the inspector's service type"}
              </span>
              {fieldErrors.vendorServiceType && <span className="isv-field-error">{fieldErrors.vendorServiceType}</span>}
            </div>
          </div>
          )}

          <div className="isv-section">
            <label className="isv-label">INVITATION DETAILS</label>

            {isBrokerageInvite ? (
              /* ── Brokerage field design (same as InviteSomeonePage) ── */
              <div className="isv-brk-form">
                <div className="isv-brk-field">
                  <label className="isv-brk-label">
                    Company name <span className="isv-brk-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`isv-brk-input${fieldErrors.brokerageCompanyName ? ' isv-brk-input-error' : ''}`}
                    placeholder="e.g. Gold Coast Realty Group LLC"
                    value={brokerageCompanyName}
                    onChange={handleFieldChange('brokerageCompanyName', setBrokerageCompanyName)}
                  />
                  {fieldErrors.brokerageCompanyName && <span className="isv-field-error">{fieldErrors.brokerageCompanyName}</span>}
                </div>

                <div className="isv-brk-field">
                  <label className="isv-brk-label">
                    Brokerage licence ID <span className="isv-brk-optional">(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="isv-brk-input"
                    placeholder="Enter brokerage licence ID if known"
                    value={brokerageLicenceId}
                    onChange={(e) => setBrokerageLicenceId(e.target.value)}
                  />
                </div>

                <div className="isv-brk-field">
                  <label className="isv-brk-label">
                    Email address <span className="isv-brk-required">*</span>
                  </label>
                  <input
                    type="email"
                    className={`isv-brk-input${fieldErrors.email ? ' isv-brk-input-error' : ''}`}
                    placeholder="Contact email address"
                    value={email}
                    onChange={handleFieldChange('email', setEmail)}
                  />
                  {fieldErrors.email && <span className="isv-field-error">{fieldErrors.email}</span>}
                </div>

                <div className="isv-brk-field">
                  <label className="isv-brk-label">
                    Welcome note <span className="isv-brk-optional">(optional)</span>
                  </label>
                  <textarea
                    className="isv-brk-textarea"
                    placeholder="Add a personal note to the invite email"
                    value={welcomeNote}
                    onChange={(e) => setWelcomeNote(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            ) : (
            <>
            {isCompanyVendor ? (
              /* Company vendor: Business name replaces first/last name (same as InviteSomeonePage) */
              <div className="isv-input-group">
                <input
                  type="text"
                  className={`isv-input ${fieldErrors.vendorBusinessName ? 'isv-input-error' : ''}`}
                  placeholder="Business name *"
                  value={vendorBusinessName}
                  onChange={handleFieldChange('vendorBusinessName', setVendorBusinessName)}
                />
                <span className="isv-input-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                    <path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" />
                  </svg>
                </span>
                {fieldErrors.vendorBusinessName && <span className="isv-field-error">{fieldErrors.vendorBusinessName}</span>}
              </div>
            ) : (
            <div className="isv-name-row">
              <div className="isv-input-group">
                <input
                  type="text"
                  className={`isv-input ${fieldErrors.firstName ? 'isv-input-error' : ''}`}
                  placeholder="First name *"
                  value={firstName}
                  onChange={handleFieldChange('firstName', setFirstName)}
                />
                <span className="isv-input-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                {fieldErrors.firstName && <span className="isv-field-error">{fieldErrors.firstName}</span>}
              </div>
              <div className="isv-input-group">
                <input
                  type="text"
                  className={`isv-input ${fieldErrors.lastName ? 'isv-input-error' : ''}`}
                  placeholder="Last name *"
                  value={lastName}
                  onChange={handleFieldChange('lastName', setLastName)}
                />
                <span className="isv-input-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                {fieldErrors.lastName && <span className="isv-field-error">{fieldErrors.lastName}</span>}
              </div>
            </div>
            )}

            <div className="isv-input-group">
              <input
                type="email"
                className={`isv-input ${fieldErrors.email ? 'isv-input-error' : ''}`}
                placeholder="Email address *"
                value={email}
                onChange={handleFieldChange('email', setEmail)}
              />
              <span className="isv-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,7 12,13 2,7" />
                </svg>
              </span>
              {fieldErrors.email && <span className="isv-field-error">{fieldErrors.email}</span>}
            </div>

            <div className="isv-input-group">
              <input
                type="tel"
                className={`isv-input ${fieldErrors.phone ? 'isv-input-error' : ''}`}
                placeholder={contactMode === 'Email' ? 'Phone number (optional)' : 'Phone number *'}
                value={phone}
                onChange={handlePhoneFieldChange(setPhone)}
              />
              <span className="isv-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E11D48" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </span>
              {fieldErrors.phone && <span className="isv-field-error">{fieldErrors.phone}</span>}
            </div>

            <div className="isv-textarea-group">
              <textarea
                className="isv-textarea"
                placeholder="Welcome note (optional)"
                value={welcomeNote}
                onChange={(e) => setWelcomeNote(e.target.value)}
                rows={3}
              />
              <span className="isv-textarea-hint">Add a personal message to the invitation</span>
            </div>
            </>
            )}
          </div>

          {/* Contact Mode — hidden for Brokerage (email-only form, same as InviteSomeonePage) */}
          {!isBrokerageInvite && (
          <div className="isv-section">
            <label className="isv-label">CONTACT MODE</label>
            <div className="isv-contact-modes">
              {['Email', 'Text', 'Both'].map((mode) => (
                <button
                  key={mode}
                  className={`isv-mode-btn ${contactMode === mode ? 'isv-mode-btn-active' : ''}`}
                  onClick={() => setContactMode(mode)}
                >
                  {mode}
                </button>
              ))}
            </div>
            <span className="isv-contact-hint">Choose how to send the invitation</span>
          </div>
          )}

          <div className="isv-divider" />

          <div className="isv-actions">
            <button
              className={`isv-copy-btn ${linkCopied ? 'isv-copy-btn-success' : ''}`}
              onClick={handleCopyLink}
              disabled={isCopyingLink || isSending}
            >
              {isCopyingLink ? (
                <><span className="isv-spinner isv-spinner-dark" />Generating Link...</>
              ) : linkCopied ? (
                <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>Link Copied!</>
              ) : (
                <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>Copy Link</>
              )}
            </button>
            <button className="isv-send-btn" onClick={handleSendInvite} disabled={isSending}>
              {isSending ? <span className="isv-spinner" /> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>}
              {isSending ? 'Sending...' : 'Send Invite'}
            </button>
          </div>

          {successMessage && (
            <div className="isv-alert isv-alert-success" style={{ marginTop: 16 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
              <span className="isv-alert-text">{successMessage}</span>
              <button className="isv-alert-dismiss" onClick={() => setSuccessMessage('')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
            </div>
          )}
          {errorMessage && (
            <div className="isv-alert isv-alert-error" style={{ marginTop: 16 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
              <span className="isv-alert-text">{errorMessage}</span>
              {pendingResendEmail && (
                <button className="isv-send-btn" style={{ marginLeft: 8, padding: '6px 14px', fontSize: 13, minWidth: 'unset' }} onClick={handleResendInvitation} disabled={isSending}>
                  {isSending ? <span className="isv-spinner" /> : 'Resend'}
                </button>
              )}
              <button className="isv-alert-dismiss" onClick={() => { setErrorMessage(''); setPendingResendEmail(null); }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
            </div>
          )}
          </>
          )}

          {/* ── TAB 2: Shareable Link ─────────────────────────────────────────── */}
          {activeTab === 'shareable' && (
            <>
              {/* Role Selection — Company SP only. Individual SP: role (Inspector)
                  and vendor type (Individual) are fixed, no Vendor Type picker. */}
              {isCompanyInviter && (
                <div className="isv-section">
                  <label className="isv-label">INVITE AS (SELECT ONE):</label>
                  <div className="isv-roles-grid">
                    {COMPANY_INVITE_ROLES.map((role) => (
                      <button
                        key={role.value}
                        className={`isv-role-btn ${shareableRole === role.value ? 'isv-role-btn-active' : ''}`}
                        onClick={() => { setShareableRole(role.value); handleNewLink(); }}
                      >
                        {role.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Vendor Type — Company SP only, when Vendor is selected */}
              {isCompanyInviter && shareableInviteRole === 'SERVICE_PROVIDER' && (
                <div className="isv-section">
                  <label className="isv-label">VENDOR TYPE</label>
                  <div className="isv-contact-modes">
                    {[{ label: 'Individual', value: 'INDIVIDUAL' }, { label: 'Company', value: 'COMPANY' }].map((opt) => (
                      <button
                        key={opt.value}
                        className={`isv-mode-btn ${shareableVendorType === opt.value ? 'isv-mode-btn-active' : ''}`}
                        onClick={() => { setShareableVendorType(opt.value); handleNewLink(); }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {shareableVendorType === 'COMPANY' && (
                    <div className="isv-input-group" style={{ marginTop: 12 }}>
                      <input
                        type="text"
                        className="isv-input"
                        placeholder="Company name (optional)"
                        value={shareableBusinessName}
                        onChange={(e) => { setShareableBusinessName(e.target.value); handleNewLink(); if (shareableError) setShareableError(''); }}
                      />
                      <span className="isv-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                          <path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" />
                        </svg>
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="isv-section">
                <label className="isv-label" style={{ marginBottom: 10 }}>MAXIMUM USES</label>
                <div className="isv-contact-modes">
                  {[
                    { label: 'Single-use', value: 'single' },
                    { label: 'Unlimited', value: 'unlimited' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      className={`isv-mode-btn ${usageLimit === opt.value ? 'isv-mode-btn-active' : ''}`}
                      onClick={() => { setUsageLimit(opt.value); handleNewLink(); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <span className="isv-contact-hint">
                  {usageLimit === 'single' ? 'Link can only be used once' : 'Link can be used unlimited times'}
                </span>
              </div>

              <div className="isv-divider" />

              {/* Generated Link Box */}
              {generatedLink ? (
                <div className="isv-link-box">
                  <div className="isv-link-box-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    <span className="isv-link-box-title">Shareable Invite Link</span>
                  </div>
                  <div className="isv-link-preview">{generatedLink}</div>
                  {linkExpiryDate && (
                    <div className="isv-link-expiry">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                      Expires {formatExpiry(linkExpiryDate)}
                    </div>
                  )}
                  <div className="isv-link-actions">
                    <button
                      className={`isv-link-action-btn ${shareableLinkCopied ? 'isv-link-action-copied' : ''}`}
                      onClick={handleCopyShareableLink}
                    >
                      {shareableLinkCopied ? (
                        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>Copied!</>
                      ) : (
                        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>Copy</>
                      )}
                    </button>
                    <button className="isv-link-action-btn" onClick={handleShareLink}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                      Share
                    </button>
                    <button className="isv-link-action-btn isv-link-action-new" onClick={handleNewLink}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.48" />
                      </svg>
                      New Link
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="isv-generate-btn"
                  onClick={handleGenerateShareableLink}
                  disabled={isGeneratingLink}
                >
                  {isGeneratingLink ? (
                    <><span className="isv-spinner" />Generating Link...</>
                  ) : (
                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>Generate Shareable Link</>
                  )}
                </button>
              )}

              {shareableError && (
                <div className="isv-alert isv-alert-error" style={{ marginTop: 16 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                  <span className="isv-alert-text">{shareableError}</span>
                  <button className="isv-alert-dismiss" onClick={() => setShareableError('')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                </div>
              )}
            </>
          )}

        </div>
      </main>
    </div>
  );
};

export default InviteInspectorPage;
